// =============================================================================
// Dashboard Data — data loading, caching, and API functions
// Extracted from make_album/dashboard.html (Data Helpers section)
// =============================================================================

import type {
  Album,
  AlbumDetail,
  PageData,
  Address,
  ChinaRegions,
  TemplateMeta,
  TemplateRegistry,
  RenderPageResult,
} from './dashboard-types';

// ========================
// Module-level state
// ========================

let _albumsCache: Album[] | null = null;

let TemplateRenderer: Promise<typeof import('./template-renderer.js')> | null = null;

let _templateDataModule: Promise<typeof import('./template-data.js')> | null = null;

export const _cssTextCache: Record<string, string> = {};

let _templateRegistryCache: TemplateRegistry | null = null;

let _templateById: Record<string, TemplateMeta> = {};

const _albumDetailCache: Record<number | string, AlbumDetail> = {};

let _chinaRegions: ChinaRegions | null = null;

// ========================
// Template renderer / data dynamic imports
// ========================

/** Dynamic import of template-renderer.js */
export function loadTemplateRenderer(): Promise<typeof import('./template-renderer.js')> {
  if (!TemplateRenderer) {
    TemplateRenderer = import('./template-renderer.js');
  }
  return TemplateRenderer;
}

/** Dynamic import of template-data.js (internal, but needed by other dashboard modules) */
export function _loadTemplateData(): Promise<typeof import('./template-data.js')> {
  if (!_templateDataModule) {
    _templateDataModule = import('./template-data.js');
  }
  return _templateDataModule;
}

// ========================
// CSS loading
// ========================

/** Load and cache template CSS text for a given album */
export function loadTemplateCSS(albumId: string, cssPath: string): Promise<string> {
  if (_cssTextCache[albumId]) return Promise.resolve(_cssTextCache[albumId]);
  if (!cssPath) return Promise.resolve('');
  return fetch('templates/' + cssPath)
    .then(function (r) { if (!r.ok) throw new Error('CSS not found'); return r.text(); })
    .then(function (css) { _cssTextCache[albumId] = css; return css; })
    .catch(function (err) { console.warn('Failed to load album CSS:', cssPath, err); return ''; });
}

// ========================
// Template registry
// ========================

/** Load and cache the official template registry */
export function loadTemplateRegistry(): Promise<TemplateRegistry> {
  if (_templateRegistryCache) return Promise.resolve(_templateRegistryCache);
  return _loadTemplateData().then(function (m) {
    return m.getOfficialRegistry({ includeHtml: false });
  }).then(function (result) {
    const data: TemplateRegistry = result.data;
    if (!data || !data.templates) throw new Error('Failed to load template registry');
    _templateRegistryCache = data;
    _templateById = {};
    (data.templates || []).forEach(function (t) { _templateById[t.id] = t; });
    return data;
  });
}

/** Look up a single template's metadata from the cached registry */
export function getTemplateMeta(templateId: string): TemplateMeta | null {
  return _templateById[templateId] || null;
}

// ========================
// Render size helpers
// ========================

/** Normalize a size value (number or {width,height} object) into a concrete {width,height} */
export function normalizeRenderSize(
  size: number | { width: number; height: number } | null | undefined,
  fallback?: number
): { width: number; height: number } {
  if (typeof size === 'number') return { width: size, height: size };
  if (size && typeof (size as { width: number }).width === 'number' && typeof (size as { height: number }).height === 'number') {
    return { width: (size as { width: number; height: number }).width, height: (size as { width: number; height: number }).height };
  }
  const fb = fallback || 400;
  return { width: fb, height: fb };
}

/** Derive the page render size from page data and/or template metadata */
export function getPageRenderSize(page: PageData | null): { width: number; height: number } {
  const meta = page && page.templateId ? getTemplateMeta(page.templateId) : null;
  return normalizeRenderSize((page && page.pageSize) || (meta && meta.pageSize) || (meta && meta.pageBaseSize), 400);
}

// ========================
// Render host helpers
// ========================

/** Clean up any previous render inside a host element */
export function cleanupRenderHost(host: HTMLElement | null): void {
  if (host && (host as any)._renderCleanup) {
    (host as any)._renderCleanup();
    (host as any)._renderCleanup = null;
  }
  if (host) host.innerHTML = '';
}

/** Render a saved page into a host element using the template renderer */
export function renderSavedPageIntoHost(
  host: HTMLElement | null,
  pageData: PageData | null,
  albumId: string
): Promise<RenderPageResult | null> {
  if (!host || !pageData) return Promise.resolve(null);
  cleanupRenderHost(host);
  const token = 'r' + Date.now() + Math.random();
  (host as any)._renderToken = token;
  const meta = getTemplateMeta(pageData.templateId || '');
  const pageSize = getPageRenderSize(pageData);
  const cssPath = (meta && meta.css_path) || pageData.cssPath || '';
  const cssAlbumId = (meta && meta.album) || albumId || cssPath;
  return loadTemplateRenderer().then(function (TR) {
    return loadTemplateCSS(cssAlbumId, cssPath).then(function (cssText) {
      return TR.renderPage({
        pageId: pageData.id ? String(pageData.id) : '',
        templateId: pageData.templateId || '',
        albumId: cssAlbumId || '',
        html: pageData.html || '',
        htmlKind: 'saved-page',
        renderClass: pageData.renderClass || (meta && meta.renderClass) || '',
        cssText: cssText,
        pageSize: pageSize,
        mode: 'dashboard',
        editable: false
      });
    });
  }).then(function (instance) {
    if ((host as any)._renderToken !== token) {
      instance.cleanup.destroy();
      return null;
    }
    host.appendChild(instance.element);
    (host as any)._renderCleanup = instance.cleanup.destroy;
    return instance;
  });
}

// ========================
// User albums API
// ========================

/** Load all user albums with caching */
export async function loadUserAlbums(): Promise<Album[]> {
  if (_albumsCache) return _albumsCache;
  const resp = await fetch('/api/albums', {
    credentials: 'include'
  });
  if (!resp.ok) throw new Error('Failed to load albums');
  const albums = await resp.json();
  _albumsCache = albums.map(function (a) {
    return {
      id: a.id,
      title: a.title,
      templateAlbumId: a.album_style,
      status: a.status === 'completed' ? 'completed' : 'editing',
      createdAt: new Date(a.created_at).getTime(),
      lastModified: new Date(a.updated_at).getTime(),
      thumbnail: a.cover_url,
      pageCount: a.page_count
    };
  });
  return _albumsCache;
}

/** Invalidate the albums cache (e.g. after create/delete) */
export function invalidateAlbumsCache(): void {
  _albumsCache = null;
}

/** Get the current albums cache (may be null if not yet loaded) */
export function getAlbumsCache(): Album[] | null {
  return _albumsCache;
}

/** Load a single album's detail from the server with caching */
export async function loadAlbumFromDB(id: number | string): Promise<AlbumDetail> {
  if (_albumDetailCache[id]) return _albumDetailCache[id];
  const resp = await fetch('/api/albums/' + id, {
    credentials: 'include'
  });
  if (!resp.ok) throw new Error('Album not found');
  const data = await resp.json();
  const spreads: Record<number, any> = [];
  if (data.pages) {
    for (let pi = 0; pi < data.pages.length; pi++) {
      const p = data.pages[pi];
      if (!spreads[p.spread_index]) spreads[p.spread_index] = {};
      if (p.is_cover) spreads[p.spread_index]._isCover = true;
      if (p.is_back_cover) spreads[p.spread_index]._isBackCover = true;
      spreads[p.spread_index][p.side] = {
        id: p.id,
        templateId: p.template_id || null,
        html: p.html_content || '',
        baseSize: p.page_base_size || 400,
        pageSize: p.page_size || null,
        renderClass: p.render_class || '',
        cssPath: p.css_path || '',
        templateCategory: p.template_category || '',
        isUnifiedSpread: p.is_unified_spread || false
      };
    }
  }
  const result: AlbumDetail = { title: data.title, albumPageBaseSize: 400, albumId: data.album_style, spreads: spreads };
  _albumDetailCache[id] = result;
  return result;
}

/** Delete an album from the server and clear caches */
export async function deleteAlbumFromDB(id: number | string): Promise<void> {
  const resp = await fetch('/api/albums/' + id, {
    method: 'DELETE',
    credentials: 'include'
  });
  if (!resp.ok) {
    const err = await resp.json().catch(function () { return {}; });
    throw new Error(err.message || '删除相册失败');
  }
  delete _albumDetailCache[id];
  invalidateAlbumsCache();
}

// ========================
// Addresses (server-first + localStorage cache)
// ========================

function _currentUserId(): string {
  const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
  return user.id || 'anonymous';
}

function _addrCacheKey(): string {
  return 'userAddresses_' + _currentUserId();
}

/** Load addresses from localStorage cache (synchronous, for rendering) */
export function loadAddresses(): Address[] {
  return JSON.parse(localStorage.getItem(_addrCacheKey()) || '[]');
}

/** Write addresses to localStorage cache */
export function saveAddressesToCache(addresses: Address[]): void {
  localStorage.setItem(_addrCacheKey(), JSON.stringify(addresses));
}

/** Save addresses to localStorage cache (deprecated — use saveAddressesToCache) */
export function saveAddresses(addresses: Address[]): void {
  saveAddressesToCache(addresses);
}

// ---- Server API functions ----

/**
 * Fetch addresses from server and hydrate localStorage cache.
 * If server has no addresses but localStorage does, upload them (auto-migration).
 * Returns the merged address list.
 */
export async function syncAddressesFromServer(): Promise<Address[]> {
  try {
    const resp = await fetch('/api/addresses', { credentials: 'include' });
    if (!resp.ok) throw new Error('Failed to fetch addresses');

    const serverAddrs: Address[] = await resp.json();

    if (serverAddrs.length > 0) {
      // Server has addresses — use them as source of truth
      const normalized = serverAddrs.map(function(a: Address) {
        return {
          id: a.id,
          name: a.name,
          phone: a.phone,
          province: a.province,
          city: a.city,
          district: a.district,
          detail: a.detail,
          isDefault: a.isDefault || false
        };
      });
      saveAddressesToCache(normalized);
      return normalized;
    }

    // Server has no addresses — check localStorage for legacy data to migrate
    const local = loadAddresses();
    if (local.length > 0) {
      // Upload legacy addresses to server
      const migrated: Address[] = [];
      for (var i = 0; i < local.length; i++) {
        var addr = local[i];
        try {
          const created = await createAddressOnServer(addr);
          migrated.push(created);
        } catch (e) {
          console.warn('Failed to migrate address to server:', e);
        }
      }
      saveAddressesToCache(migrated);
      return migrated;
    }

    return [];
  } catch (e) {
    console.warn('syncAddressesFromServer failed, falling back to localStorage:', e);
    return loadAddresses();
  }
}

/** Create an address on the server */
export async function createAddressOnServer(addr: Address): Promise<Address> {
  const resp = await fetch('/api/addresses', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: addr.name,
      phone: addr.phone,
      province: addr.province,
      city: addr.city,
      district: addr.district,
      detail: addr.detail,
      is_default: addr.isDefault || false
    })
  });
  if (!resp.ok) {
    const err = await resp.json().catch(function() { return { message: 'Unknown error' }; });
    throw new Error(err.message || 'Failed to create address');
  }
  return resp.json();
}

/** Update an address on the server */
export async function updateAddressOnServer(id: number, addr: Partial<Address>): Promise<Address> {
  const resp = await fetch('/api/addresses/' + id, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(addr)
  });
  if (!resp.ok) {
    const err = await resp.json().catch(function() { return { message: 'Unknown error' }; });
    throw new Error(err.message || 'Failed to update address');
  }
  return resp.json();
}

/** Delete an address on the server */
export async function deleteAddressOnServer(id: number): Promise<void> {
  const resp = await fetch('/api/addresses/' + id, {
    method: 'DELETE',
    credentials: 'include'
  });
  if (!resp.ok) {
    const err = await resp.json().catch(function() { return { message: 'Unknown error' }; });
    throw new Error(err.message || 'Failed to delete address');
  }
}

// ========================
// China regions
// ========================

/** Load china regions data with caching */
export function loadChinaRegions(): Promise<ChinaRegions> {
  if (_chinaRegions) return Promise.resolve(_chinaRegions);
  return fetch('/make_album/data/china-regions.json')
    .then(function (r) { return r.json(); })
    .then(function (data) { _chinaRegions = data; return data; })
    .catch(function (e) { console.warn('Failed to load china regions:', e); return []; });
}
